import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { AI_OVERVIEW_EVENT_NAME, DataCloudEventTypeValues } from '~/modules/analytics/datacloudEventTypes/constants';
import { DataCloudEventType, DataCloudIdentityData } from '~/modules/analytics/datacloudEventTypes/datacloudEventType';
import { DataCloudEventData } from '~/modules/services/analyticsService';
import { randomUUID } from '~/modules/utilities/cross_env_utils';

import { AiOverviewFeedbackStatus } from './aiOverviewFeedbackDataCloudEventType';

const NEXT_KEYWORD_MAX = 200;

export interface AiOverviewSuggestedClickDataCloudAnalyticsEvent {
    interaction: {
        name: typeof AI_OVERVIEW_EVENT_NAME;
        eventType: typeof DataCloudEventTypeValues.aiOverviewSuggestedClick;
        deviceId: string;
        searchQueryUid: string;
        'co-relation-id': string;
        aiOverviewStatus: AiOverviewFeedbackStatus;
        aiOverviewNextKeyword: string;
        aiOverviewSuggestionPosition: number;
        anonymous?: '0' | '1';
        language?: string;
        userAgent?: string;
        source?: string;
        searchString?: string;
        TokenCount?: number;
        TTFT?: number;
    };
    user: {
        attributes: DataCloudIdentityData & {
            eventType: string;
            deviceId: string;
        };
    };
}

export interface AiOverviewSuggestedClickEventData extends DataCloudEventData {
    aiOverviewNextKeyword?: string;
    aiOverviewSuggestionPosition?: number;
    aiOverviewStatus?: AiOverviewFeedbackStatus;
    aiOverviewTokenCount?: number;
    aiOverviewTtft?: number;
    aiOverviewCorrelationId?: string;
    searchQueryUid?: string;
    searchString?: string;
    source?: string;
}

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

const normalizeKeyword = (raw: string): string =>
    raw
        .replace(/[\r\n]+/g, ' ')
        .trim()
        .slice(0, NEXT_KEYWORD_MAX);

export class AiOverviewSuggestedClickDataCloudEventType extends DataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_AI_OVERVIEW_SUGGESTED_CLICK;
    }

    getBody(event: AiOverviewSuggestedClickEventData): AiOverviewSuggestedClickDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const status: AiOverviewFeedbackStatus = event.aiOverviewStatus ?? 'idle';
        const nextKeyword = normalizeKeyword(event.aiOverviewNextKeyword ?? '');
        const position = isFiniteNumber(event.aiOverviewSuggestionPosition)
            ? Math.max(0, event.aiOverviewSuggestionPosition)
            : 0;

        const interaction: AiOverviewSuggestedClickDataCloudAnalyticsEvent['interaction'] = {
            name: AI_OVERVIEW_EVENT_NAME,
            eventType: DataCloudEventTypeValues.aiOverviewSuggestedClick,
            deviceId: baseEvent.username,
            searchQueryUid: event.searchQueryUid ?? '',
            'co-relation-id': event.aiOverviewCorrelationId || randomUUID(),
            aiOverviewStatus: status,
            aiOverviewNextKeyword: nextKeyword,
            aiOverviewSuggestionPosition: position,
            anonymous: baseEvent.anonymous ? '1' : '0',
            language: baseEvent.language,
            userAgent: baseEvent.userAgent,
        };

        const source = event.source?.trim();
        if (source) {
            interaction.source = source;
        }

        const searchString = event.searchString?.trim();
        if (searchString) {
            interaction.searchString = searchString;
        }

        if (isFiniteNumber(event.aiOverviewTokenCount)) {
            interaction.TokenCount = clamp(event.aiOverviewTokenCount, 0, 100_000);
        }

        if (isFiniteNumber(event.aiOverviewTtft)) {
            interaction.TTFT = clamp(event.aiOverviewTtft, 0, 60_000);
        }

        return {
            interaction,
            user: {
                attributes: {
                    ...baseEvent.userData,
                    eventType: DataCloudEventTypeValues.identity,
                    deviceId: baseEvent.username,
                },
            },
        };
    }
}
